import { Flex } from '@chakra-ui/layout';
import { Button, useDisclosure } from '@chakra-ui/react';
import { maskitoTransform } from '@maskito/core';
import {
  CellContext,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
} from '@tanstack/react-table';
import React, { useEffect } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';

import { CommitmentMechanicsFormData, ServicingFeesData } from './types';
import { DeleteIconButton } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  TextInput,
  USD_MASK_OPTIONS,
} from 'packages/common-frontend/components/InputsBETA/TextInput';
import { Typography } from 'packages/common-frontend/components/Typography';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import { remToPx } from 'packages/common-frontend/theme/utils';
import { useServicingFeeDiffResult } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/hooks';
import { AddLenderModal } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/LenderTable/AddLenderModal';
import { useConfirmDeleteContext } from '~/core/routes/settings/implementations/common/ConfirmDeleteModalContext';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import {
  DiffTag,
  PlaceholderInput,
  ShowWhileEditing,
} from '~/core/routes/settings/implementations/common/DiffInputs';

export const ServicingFeeInputCell = ({
  row,
}: CellContext<ServicingFeesData, any>) => {
  const diff = useServicingFeeDiffResult(row.original.lenderId);
  const isEditing = useCreditAgreementStore((store) => store.isEditing);

  if (!isEditing) {
    const value =
      diff.currentFee?.servicingFee ?? diff.previousFee?.servicingFee;

    return (
      <PlaceholderInput
        value={maskitoTransform(String(value ?? ''), USD_MASK_OPTIONS)}
        tag={<DiffTag diff={diff.diffResult} />}
      />
    );
  }

  return (
    <TextInput<CommitmentMechanicsFormData>
      name={`wipForm.servicingFees.${row.index}.servicingFee`}
      mask="percentage"
      required
      isDisabled={!isEditing}
    />
  );
};

// Define the meta type for tanstack table
// The tanstack docs show a different way to type this property, but it is global
// to all tables. So we will just do it this way instead. See: https://tanstack.com/table/v8/docs/api/core/table#meta
type LenderTableMeta = {
  remove: (index: number) => void;
};

const RemoveLenderCell = ({
  row,
  table,
}: CellContext<ServicingFeesData, any>) => {
  // Access the meta property with proper typing
  const meta = table.options.meta as LenderTableMeta;

  const name = row.original.lenderName;
  const { showConfirmDeleteModal } = useConfirmDeleteContext();
  const onClick = () =>
    showConfirmDeleteModal(name, () => meta.remove(row.index));

  return (
    <ShowWhileEditing>
      <Flex justify="right">
        <DeleteIconButton label="Remove Servicing Fee" onClick={onClick} />
      </Flex>
    </ShowWhileEditing>
  );
};

const columnHelper = createColumnHelper<ServicingFeesData>();

const columnDefs = [
  columnHelper.accessor('lenderName', {
    header: 'Lender',
    enableSorting: false,
  }),
  columnHelper.accessor('servicingFee', {
    id: 'servicingFee',
    header: 'Servicing Fee',
    enableSorting: false,
    cell: ServicingFeeInputCell,
  }),
  columnHelper.display({
    id: 'removeProduct',
    header: '',
    size: remToPx(1),
    enableSorting: false,
    cell: RemoveLenderCell,
  }),
];

export const ServicingFeeTable = ({
  formKey,
}: {
  formKey: 'wipForm' | 'diffForm';
}) => {
  const { append, remove, fields } = useFieldArray<
    CommitmentMechanicsFormData,
    'wipForm.servicingFees' | 'diffForm.servicingFees'
  >({
    name: `${formKey}.servicingFees`,
  });

  const lenders = useWatch<CommitmentMechanicsFormData, 'wipForm.lenders'>({
    name: 'wipForm.lenders',
  });

  const isEditing = useCreditAgreementStore((store) => store.isEditing);

  // This removes any lenders from the Servicing Fee Table that aren't
  // also present in the Lenders table
  useEffect(() => {
    if (isEditing) {
      const lendersSet = new Set(lenders.map((f) => f.lenderId));
      fields.forEach((field, idx) => {
        if (!lendersSet.has(field.lenderId)) {
          remove(idx);
        }
      });
    }
  }, [fields, lenders, remove, isEditing]);

  const disclosure = useDisclosure();

  const table = useReactTable({
    data: fields,
    columns: columnDefs,
    meta: { remove } as LenderTableMeta,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.lenderId,
  });

  return (
    <Flex direction="column" gap="1rem">
      <Flex justifyContent="space-between" direction="column" gap="1rem">
        <Flex alignItems="center" justifyContent="space-between">
          <Typography variant="section-title" trim>
            Servicing Fees
          </Typography>
          <ShowWhileEditing>
            <Button
              colorScheme="v2-aurora"
              variant="secondary"
              leftIcon={<Icon icon="plus" />}
              onClick={disclosure.onOpen}
            >
              Add Lender
            </Button>
          </ShowWhileEditing>
        </Flex>
        <Typography variant="base-muted">
          The servicing fee is a percentage of the borrower&apos;s interest
          payment, deducted before distribution to lenders for ongoing loan
          administration. The administrative agent is excluded.
        </Typography>
        <AddLenderModal
          disclosure={disclosure}
          onAddLender={(lender) => append({ servicingFee: 0, ...lender })}
          isServicingFeeTable
        />
      </Flex>
      {fields.length > 0 && <TableBETA table={table} />}
    </Flex>
  );
};
